var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import moment from 'moment';
import { CURRENT_CLAIM_TYPE_LAYOUT_COLOR } from '@declaration/layout';
import TitleImageDescriptionLayout from '@declaration/components/TitleImageDescriptionLayout.vue';
import { quitClaimDeclarationCloseCallback } from '@declaration/helpers/layoutMetaCallback';
import { ChargeTypology } from '@shared/types/api/charge';
import { ClaimFileType } from '@shared/types/file';
import FileCollectionUpload from '@declaration/components/upload/FileCollectionUpload.vue';
import AmountInput from '@declaration/components/UI/AmountInput.vue';
import { InsurerFeatureFlag, insurerHasFeature } from '@shared/featureFlags/insurerFeatureFlags';
import { newClientIdentifier } from '@shared/utils/clientIdentifier';
import { ApiEntityType } from '@shared/types/api/apiEntityType';
import { Step } from '@shared/claim/steps';
const claim = namespace('claim');
const qualification = namespace('qualification');
const counting = namespace('counting');
const helpers = namespace('helpers');
const settings = namespace('settings');
let ShouldLocalizeLeakage = class ShouldLocalizeLeakage extends Vue {
    constructor() {
        super(...arguments);
        this.fileType = ClaimFileType.ProfessionalIntervention;
        this.invoice = null;
        this.invoiceAmount = null;
    }
    get helpNeeded() {
        return !!this.qualification.isHelpToFindLeakageNeeded;
    }
    get illustration() {
        return this.helpNeeded
            ? require('@declaration/assets/images/appointment/assistance.svg')
            : require('@declaration/assets/images/documents/pause-parcours.svg');
    }
    get questionTitle() {
        const key = this.helpNeeded ? 'help-needed' : 'no-help-needed';
        return this.$tc(`step.qualification/attente-localisation-fuite.${key}.panel-title`);
    }
    get description() {
        const key = this.helpNeeded ? 'help-needed' : 'no-help-needed';
        const maxDelay = this.setting('timeInDaysBeforeClaimAdminNoFurtherAction');
        const diff = this.startedAt ? moment(Date.now()).diff(moment(this.startedAt), 'days') : 0;
        const delayInDays = this.helpNeeded ? null : maxDelay - diff;
        const tcKey = `step.qualification/attente-localisation-fuite.${key}.description`;
        const tcParams = this.helpNeeded && insurerHasFeature(InsurerFeatureFlag.ShowTimeDelayBeforeClosure)
            ? { days: delayInDays }
            : undefined;
        return this.$tc(tcKey, undefined, tcParams);
    }
    get buttonLabel() {
        return this.$tc('step.qualification/attente-localisation-fuite.validation-label');
    }
    get uploadLabel() {
        return this.$tc('step.qualification/attente-localisation-fuite.upload-button-label');
    }
    get addFileLabel() {
        return this.$tc('step.qualification/attente-localisation-fuite.add-file-label');
    }
    get canValidate() {
        if (this.helpNeeded || !this.invoice || this.invoice.uploadedAppFiles.length === 0)
            return true;
        if (this.invoiceAmount === null || this.invoiceAmount <= 0)
            return false;
        return true;
    }
    mounted() {
        var _a, _b;
        const existingCharge = this.counting.charges.find((c) => c.typology === ChargeTypology.LeakageDetectionFees);
        if (existingCharge) {
            this.invoice = (_a = existingCharge.invoice) !== null && _a !== void 0 ? _a : null;
            this.invoiceAmount = (_b = existingCharge.amount) !== null && _b !== void 0 ? _b : null;
        }
    }
    async validate() {
        const charges = this.updateCharges();
        await this.updateCounting({ charges });
        await this.updateQualification({ qualificationPayload: { isWaterLeakageCauseLocalized: true } });
        const payload = {
            step: Step.ShouldLocalizeLeakage,
            counting: {
                id: this.counting.id,
                charges,
            },
            waterDamageQualification: {
                id: this.qualification.id,
                isWaterLeakageCauseLocalized: true,
            },
        };
        await this.saveClaimWithPayload(payload);
    }
    updateCharges() {
        const updatedCharges = this.counting.charges.map((c) => { var _a; return (_a = c.id) !== null && _a !== void 0 ? _a : c; });
        const existingCharge = this.counting.charges.find((c) => c.typology === ChargeTypology.LeakageDetectionFees);
        if (this.helpNeeded) {
            // Si on a besoin d'aide, on s'assure de supprimer la charge de frais de détection de fuite si elle existe
            if (!existingCharge)
                return updatedCharges;
            return updatedCharges.filter((c) => {
                if (typeof c === 'string')
                    return c !== existingCharge.id;
                return c.id !== existingCharge.id;
            });
        }
        if (this.invoice && this.invoice.uploadedAppFiles.length > 0) {
            // Si on a une facture, on ajoute ou met à jour la charge de frais de détection de fuite
            if (existingCharge) {
                // Si la charge existe déjà, on la met à jour
                const existingIndex = this.counting.charges.indexOf(existingCharge);
                updatedCharges.splice(existingIndex, 1, {
                    id: existingCharge.id,
                    invoice: this.invoice,
                    amount: this.invoiceAmount,
                });
            }
            else {
                // Sinon, on ajoute une nouvelle charge
                updatedCharges.push({
                    '@type': ApiEntityType.Charge,
                    clientIdentifier: newClientIdentifier(),
                    typology: ChargeTypology.LeakageDetectionFees,
                    invoice: this.invoice,
                    amount: this.invoiceAmount,
                });
            }
        }
        else if (existingCharge) {
            // Si on n'a pas de facture mais qu'une charge existe, on la supprime
            const existingIndex = this.counting.charges.indexOf(existingCharge);
            if (existingIndex !== -1)
                updatedCharges.splice(existingIndex, 1);
        }
        return updatedCharges;
    }
};
__decorate([
    claim.State('isUpdatingClaim')
], ShouldLocalizeLeakage.prototype, "isUpdatingClaim", void 0);
__decorate([
    counting.State('counting')
], ShouldLocalizeLeakage.prototype, "counting", void 0);
__decorate([
    qualification.State('qualification')
], ShouldLocalizeLeakage.prototype, "qualification", void 0);
__decorate([
    qualification.State('typeTrigram')
], ShouldLocalizeLeakage.prototype, "typeTrigram", void 0);
__decorate([
    claim.Getter('claimStartedAt')
], ShouldLocalizeLeakage.prototype, "startedAt", void 0);
__decorate([
    settings.Getter('setting')
], ShouldLocalizeLeakage.prototype, "setting", void 0);
__decorate([
    qualification.Action('updateQualification')
], ShouldLocalizeLeakage.prototype, "updateQualification", void 0);
__decorate([
    counting.Action('updateCounting')
], ShouldLocalizeLeakage.prototype, "updateCounting", void 0);
__decorate([
    claim.Action('saveClaimWithPayload')
], ShouldLocalizeLeakage.prototype, "saveClaimWithPayload", void 0);
__decorate([
    helpers.Action('show')
], ShouldLocalizeLeakage.prototype, "showHelp", void 0);
ShouldLocalizeLeakage = __decorate([
    Component({
        components: {
            TitleImageDescriptionLayout,
            FileCollectionUpload,
            AmountInput,
        },
        meta: {
            title: 'page.title.confirmation',
            color: CURRENT_CLAIM_TYPE_LAYOUT_COLOR,
            close: quitClaimDeclarationCloseCallback,
        },
    })
], ShouldLocalizeLeakage);
export default ShouldLocalizeLeakage;
